import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import logo from '../../assets/logo.png';
import { AnimatePresence, motion } from 'framer-motion';
import ThemeToggle from '../ThemeToggle';
import {
  FaCheckCircle,
  FaChevronRight,
  FaChevronDown,
  FaArrowLeft,
  FaArrowRight,
  FaClock,
  FaTrophy,
  FaLightbulb,
  FaBars
} from 'react-icons/fa';
import { ArrowRight, HelpCircle, X } from 'lucide-react';
import ParticleBackground from '../common/ParticleBackground';

const SQL_TOUR_STEPS = [
  {
    id: 'business-request',
    title: 'The Business Scenario',
    content: 'Every challenge opens with a real business context - a company, a problem, and a reason it matters. Read this first. It shapes what your query should do.',
    target: 'sql-tour-business-request',
    position: 'right',
  },
  {
    id: 'task',
    title: 'Your Task',
    content: 'The Task box tells you exactly what your SQL query must return. The phrasing is precise - match it closely when writing your query.',
    target: 'sql-tour-task',
    position: 'bottom',
  },
  {
    id: 'concept-tab',
    title: 'Concept & Syntax',
    content: 'Click the Concept tab to see the SQL concept you need for this challenge - with syntax examples and explanations. Study it before you start writing.',
    target: 'sql-tour-concept-tab',
    position: 'bottom',
  },
  {
    id: 'expected-tab',
    title: 'Expected Output',
    content: 'The Expected Output tab shows you the exact columns your query must return. Check it before verifying - if the column names or types don\'t match, your answer won\'t pass.',
    target: 'sql-tour-expected-tab',
    position: 'bottom',
  },
  {
    id: 'schema',
    title: 'Explore the Schema',
    content: 'Click any table tab here to preview its data. Knowing the column names, types, and sample rows is essential for writing correct SQL.',
    target: 'sql-tour-schema',
    position: 'left',
  },
  {
    id: 'run',
    title: 'Run Your Query',
    content: 'Click Run (or press ⌘ + Enter) to execute your SQL against the real dataset. You can run as many times as you like - experiment freely.',
    target: 'sql-tour-run',
    position: 'left',
  },
  {
    id: 'verify',
    title: 'Verify Your Answer',
    content: 'When you\'re confident, click Run & Verify. It executes your query and checks it against the expected output. Your answer must match exactly - no partial credit.',
    target: 'sql-tour-verify',
    position: 'left',
  },
  {
    id: 'results',
    title: 'Results Panel',
    content: 'Your query output appears here. Check the column names and row count match what the Task asked for. If something looks off, adjust your query and run again.',
    target: 'sql-tour-results',
    position: 'top',
  },
  {
    id: 'hint',
    title: 'Need a Hint?',
    content: 'Stuck? Click Show Hint to get a nudge in the right direction without revealing the full answer. A good hint can unlock your thinking.',
    target: 'sql-tour-hint',
    position: 'bottom',
  },
  {
    id: 'solution',
    title: 'Full Solution',
    content: 'Click Solution to see the complete working query with a full explanation. Use it to learn - not just to copy.',
    target: 'sql-tour-solution',
    position: 'bottom',
  },
];

const CARD_WIDTH = 340;
const CARD_PADDING = 16;

const SQLTourOverlay = ({ stepIndex, onNext, onPrev, onClose }) => {
  const step = SQL_TOUR_STEPS[stepIndex];
  const [targetRect, setTargetRect] = React.useState(null);

  React.useEffect(() => {
    const updateRect = () => {
      if (step.target) {
        const el = document.getElementById(step.target);
        if (el) {
          el.scrollIntoView({ behavior: 'auto', block: 'nearest' });
          requestAnimationFrame(() => {
            const rect = el.getBoundingClientRect();
            setTargetRect({ top: rect.top, left: rect.left, width: rect.width, height: rect.height });
          });
        } else {
          setTargetRect(null);
        }
      } else {
        setTargetRect(null);
      }
    };
    updateRect();
    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);
    return () => {
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
    };
  }, [step]);

  const getCardStyle = () => {
    if (!targetRect) return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
    const pos = step.position || 'bottom';
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const cardH = 200;

    let style = {};
    if (pos === 'bottom') {
      let left = targetRect.left + targetRect.width / 2 - CARD_WIDTH / 2;
      left = Math.max(CARD_PADDING, Math.min(left, vw - CARD_WIDTH - CARD_PADDING));
      style = { top: Math.min(targetRect.top + targetRect.height + 12, vh - cardH - CARD_PADDING), left };
    } else if (pos === 'top') {
      let left = targetRect.left + targetRect.width / 2 - CARD_WIDTH / 2;
      left = Math.max(CARD_PADDING, Math.min(left, vw - CARD_WIDTH - CARD_PADDING));
      style = { top: Math.max(CARD_PADDING, targetRect.top - cardH - 12), left };
    } else if (pos === 'right') {
      let top = targetRect.top + targetRect.height / 2 - cardH / 2;
      top = Math.max(CARD_PADDING, Math.min(top, vh - cardH - CARD_PADDING));
      style = { top, left: Math.min(targetRect.left + targetRect.width + 12, vw - CARD_WIDTH - CARD_PADDING) };
    } else if (pos === 'left') {
      let top = targetRect.top + targetRect.height / 2 - cardH / 2;
      top = Math.max(CARD_PADDING, Math.min(top, vh - cardH - CARD_PADDING));
      style = { top, left: Math.max(CARD_PADDING, targetRect.left - CARD_WIDTH - 12) };
    }
    return style;
  };

  return (
    <AnimatePresence>
      <motion.div
        key="sql-tour-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] pointer-events-auto"
        style={{ pointerEvents: 'auto' }}
      >
        {/* Dark overlay with spotlight cutout */}
        {targetRect ? (
          <div
            className="absolute inset-0"
            style={{
              background: 'rgba(0,0,0,0.72)',
              WebkitMaskImage: `radial-gradient(ellipse ${targetRect.width + 24}px ${targetRect.height + 24}px at ${targetRect.left + targetRect.width / 2}px ${targetRect.top + targetRect.height / 2}px, transparent 70%, black 100%)`,
              maskImage: `radial-gradient(ellipse ${targetRect.width + 24}px ${targetRect.height + 24}px at ${targetRect.left + targetRect.width / 2}px ${targetRect.top + targetRect.height / 2}px, transparent 70%, black 100%)`,
            }}
            onClick={onClose}
          />
        ) : (
          <div className="absolute inset-0 bg-black/72" onClick={onClose} />
        )}

        {/* Spotlight ring around target */}
        {targetRect && (
          <div
            className="absolute rounded-xl pointer-events-none"
            style={{
              top: targetRect.top - 6,
              left: targetRect.left - 6,
              width: targetRect.width + 12,
              height: targetRect.height + 12,
              boxShadow: '0 0 0 3px rgba(249,115,22,0.8), 0 0 0 6px rgba(249,115,22,0.2)',
              transition: 'all 0.2s ease',
            }}
          />
        )}

        {/* Tour card */}
        <motion.div
          key={step.id}
          initial={{ opacity: 0, scale: 0.92, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.92 }}
          transition={{ type: 'spring', stiffness: 320, damping: 26 }}
          className="absolute bg-white dark:bg-[#1e293b] rounded-2xl shadow-2xl border border-gray-200 dark:border-white/10 p-5 z-10"
          style={{ width: CARD_WIDTH, ...getCardStyle() }}
        >
          <button onClick={onClose} className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors">
            <X size={15} />
          </button>
          <div className="flex items-center gap-2 mb-2.5">
            <span className="bg-orange-100 dark:bg-orange-500/20 text-orange-600 dark:text-orange-400 text-[10px] font-bold px-2 py-0.5 rounded-full">
              STEP {stepIndex + 1} / {SQL_TOUR_STEPS.length}
            </span>
          </div>
          <h3 className="text-base font-bold text-gray-900 dark:text-white mb-1.5">{step.title}</h3>
          <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed mb-4">{step.content}</p>
          <div className="flex items-center justify-between">
            <button
              onClick={onPrev}
              disabled={stepIndex === 0}
              className={`text-sm font-medium ${stepIndex === 0 ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed' : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'}`}
            >
              Back
            </button>
            <button
              onClick={onNext}
              className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-bold shadow-lg shadow-orange-500/20 transition-all flex items-center gap-2"
            >
              {stepIndex === SQL_TOUR_STEPS.length - 1 ? 'Get Started' : 'Next'}
              <ArrowRight size={14} />
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

/**
 * ExerciseLayout - Industry-standard learning interface component
 * 
 * Features:
 * - Collapsible sidebar with progress tracking
 * - Case navigation with completion status
 * - Automatic session persistence (localStorage + database)
 * - Progress indicators (percentage, checkmarks)
 * - Responsive mobile-first design
 * - Resume from last position
 */
const ExerciseLayout = ({
  title,
  cases,
  currentCase,
  onCaseChange,
  completedCases,
  children,
  totalTime,
  completionPercentage,
  completedChallenges = {},
  onChallengeClick,
  currentChallenge = 0,
  onPrevChallenge,
  onNextChallenge,
  canGoPrev = false,
  canGoNext = false,
  onTitleClick,
  isIntro = false,
}) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedCases, setExpandedCases] = useState(new Set([currentCase]));
  const currentCaseRef = React.useRef(null);

  // Tour state
  const [tourIndex, setTourIndex] = useState(-1);

  useEffect(() => {
    const seen = localStorage.getItem('datahelix_sql_tour_seen');
    // Only auto-start the tour when the user is viewing an actual question, not the intro screen
    if (!seen && !isIntro) setTourIndex(0);
  }, [isIntro]);

  const handleTourNext = () => {
    if (tourIndex < SQL_TOUR_STEPS.length - 1) {
      setTourIndex(prev => prev + 1);
    } else {
      completeSQLTour();
    }
  };
  const handleTourPrev = () => { if (tourIndex > 0) setTourIndex(prev => prev - 1); };
  const completeSQLTour = () => {
    setTourIndex(-1);
    localStorage.setItem('datahelix_sql_tour_seen', 'true');
  };
  const startTour = () => { setSidebarOpen(false); setTourIndex(0); };

  // Suppress harmless ResizeObserver errors (common when toggling sidebar)
  // This MUST run first, before any other effects
  useEffect(() => {
    // Store original handlers
    const originalError = console.error;
    const originalWindowError = window.onerror;

    // Override window.onerror to catch errors before React's error boundary
    window.onerror = (message, source, lineno, colno, error) => {
      if (message && message.toString().includes('ResizeObserver loop')) {
        // Silently suppress ResizeObserver errors
        return true; // Prevent default error handling
      }
      // Call original handler for other errors
      if (originalWindowError) {
        return originalWindowError(message, source, lineno, colno, error);
      }
      return false;
    };

    // Also override console.error to suppress console output
    console.error = (...args) => {
      const message = args[0]?.toString() || '';
      if (message.includes('ResizeObserver loop')) {
        return; // Silently suppress
      }
      originalError.apply(console, args);
    };

    // Event listener as backup
    const errorHandler = (e) => {
      if (e.message && e.message.includes('ResizeObserver loop')) {
        e.stopImmediatePropagation();
        e.preventDefault();
      }
    };
    window.addEventListener('error', errorHandler, true); // Use capture phase

    return () => {
      window.onerror = originalWindowError;
      console.error = originalError;
      window.removeEventListener('error', errorHandler, true);
    };
  }, []);

  // Auto-expand current case and collapse others when modal opens
  useEffect(() => {
    if (sidebarOpen) {
      // When modal opens, only expand the current case
      setExpandedCases(new Set([currentCase]));
    }
  }, [sidebarOpen, currentCase]);

  // Auto-scroll to current case when modal opens
  useEffect(() => {
    if (sidebarOpen && currentCaseRef.current) {
      // Small delay to ensure DOM is rendered
      setTimeout(() => {
        currentCaseRef.current?.scrollIntoView({
          behavior: 'auto',
          block: 'center'
        });
      }, 100);
    }
  }, [sidebarOpen]);

  const toggleCase = (caseIndex) => {
    const newExpanded = new Set(expandedCases);
    if (newExpanded.has(caseIndex)) {
      newExpanded.delete(caseIndex);
    } else {
      newExpanded.add(caseIndex);
    }
    setExpandedCases(newExpanded);
  };

  const handleCaseClick = (caseIndex) => {
    onCaseChange(caseIndex);
    setSidebarOpen(false); // Always close modal on navigation
  };

  return (
    <div className="h-screen w-screen bg-white dark:bg-[#0a1220] overflow-hidden flex flex-col font-sans transition-colors duration-300">

      {/* Tour Overlay */}
      {tourIndex >= 0 && (
        <SQLTourOverlay
          stepIndex={tourIndex}
          onNext={handleTourNext}
          onPrev={handleTourPrev}
          onClose={completeSQLTour}
        />
      )}
      {/* Cases Modal Overlay - Centered */}
      {sidebarOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-40 animate-fadeIn"
            onClick={() => setSidebarOpen(false)}
          />

          {/* Modal */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
            <div className="bg-white dark:bg-[#162032] rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden pointer-events-auto animate-scaleIn flex flex-col border border-gray-200 dark:border-gray-700">
              {/* Modal Header */}
              <div className="bg-gray-100 dark:bg-gradient-to-r dark:from-[#1e3a5f] dark:to-[#0a1220] p-6 text-gray-900 dark:text-white flex-shrink-0 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-bold bg-gradient-to-r from-amber-300 via-orange-500 to-red-500 text-transparent bg-clip-text inline-block">
                    {title}
                  </h2>
                  <button
                    onClick={() => setSidebarOpen(false)}
                    className="text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-white transition-colors text-2xl leading-none w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-200 dark:hover:bg-white/5"
                    aria-label="Close modal"
                  >
                    ✕
                  </button>
                </div>

                {/* Progress Summary */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 text-gray-600 dark:text-gray-300 font-medium">
                      <FaTrophy className="text-amber-500 dark:text-amber-400" />
                      Progress
                    </span>
                    <span className="font-bold text-gray-900 dark:text-white">{completionPercentage}%</span>
                  </div>

                  {/* Progress Bar */}
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                    <div
                      className="bg-gradient-to-r from-orange-500 to-red-500 h-full rounded-full transition-all duration-500 ease-out"
                      style={{ width: `${completionPercentage}%` }}
                    />
                  </div>

                  <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                    <span>{completedCases.size} of {cases.length} cases completed</span>
                    {totalTime && (
                      <span className="flex items-center gap-1">
                        <FaClock size={10} />
                        {totalTime}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Cases List - Scrollable */}
              <nav className="flex-1 overflow-y-auto p-4 space-y-2 custom-scrollbar">
                {cases.map((caseItem, index) => {
                  const isExpanded = expandedCases.has(index);
                  const isCompleted = completedCases.has(index);
                  const isCurrent = index === currentCase;

                  return (
                    <div
                      key={index}
                      className="space-y-1"
                      ref={isCurrent ? currentCaseRef : null}
                    >
                      <div
                        className={`w-full flex items-center gap-3 p-3 rounded-lg transition-all duration-200 border ${isCurrent
                          ? 'bg-orange-50 border-orange-200 text-orange-900 dark:bg-orange-500/10 dark:border-orange-500/30'
                          : 'bg-white border-gray-100 hover:bg-gray-50 hover:border-gray-200 dark:bg-[#0a1220] dark:border-gray-800 dark:hover:bg-[#1e293b] dark:hover:border-gray-700'
                          }`}
                      >
                        {/* Clickable area for navigation */}
                        <button
                          onClick={() => handleCaseClick(index)}
                          className="flex items-center gap-3 flex-1 text-left"
                        >
                          {/* Case Number */}
                          <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm transition-colors ${isCurrent
                            ? 'bg-gradient-to-br from-orange-500 to-red-500 text-white shadow-lg shadow-orange-500/20'
                            : 'bg-gray-100 text-gray-500 border border-gray-200 dark:bg-gray-800 dark:text-gray-500 dark:border-gray-700'
                            }`}>
                            {index + 1}
                          </div>

                          {/* Case Info */}
                          <div className="flex-1 min-w-0">
                            <div className={`font-bold text-sm line-clamp-1 transition-colors ${isCurrent ? 'text-orange-900 dark:text-white' : 'text-gray-700 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-gray-200'}`}>
                              {(() => {
                                const fullTitle = caseItem.title.includes(':') ? caseItem.title.split(':')[1].trim() : caseItem.title;
                                const parts = fullTitle.split(/(?<=PaperPlus|MobileApp|SQL Primer)/);
                                if (parts.length > 1) {
                                  return `${parts[0]} -${parts[1]}`;
                                }
                                return fullTitle;
                              })()}
                            </div>
                            {/* Category Badge */}
                            <div className={`mt-1.5 inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider transition-colors ${isCurrent
                              ? 'bg-orange-100 text-orange-800 border border-orange-200 dark:bg-orange-500/20 dark:text-orange-400 dark:border-orange-500/30 shadow-sm'
                              : 'bg-orange-50/50 text-orange-900/60 border border-orange-100/50 dark:bg-orange-500/5 dark:text-orange-200/50 dark:border-orange-500/10 hover:bg-orange-100 hover:border-orange-200 dark:hover:bg-orange-500/10 dark:hover:text-orange-200 dark:hover:border-orange-500/20'
                              }`}>
                              {caseItem.category}
                            </div>
                          </div>

                          {/* Completion tick */}
                          {isCompleted && (
                            <div className="flex-shrink-0">
                              <FaCheckCircle className="text-gray-300 dark:text-gray-600" size={14} />
                            </div>
                          )}
                        </button>

                        {/* Expand Icon */}
                        {caseItem.challenges && (
                          <button
                            onClick={() => toggleCase(index)}
                            className="flex-shrink-0 p-2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-white transition-colors"
                            aria-label="Toggle challenges"
                          >
                            {isExpanded ? (
                              <FaChevronDown size={12} />
                            ) : (
                              <FaChevronRight size={12} />
                            )}
                          </button>
                        )}
                      </div>

                      {/* Challenges List (if expanded) */}
                      {isExpanded && caseItem.challenges && (
                        <div className="ml-5 pl-4 border-l border-gray-200 dark:border-gray-800 space-y-1 my-1">
                          {caseItem.challenges.map((challenge, challengeIndex) => {
                            const caseKey = `case_${caseItem.id}`;
                            const isChallengeCompleted = completedChallenges[caseKey]?.includes(challenge.id);
                            const isCurrentChallenge = index === currentCase && challengeIndex === currentChallenge;

                            return (
                              <button
                                key={challengeIndex}
                                onClick={() => {
                                  handleCaseClick(index);
                                  if (onChallengeClick) {
                                    onChallengeClick(index, challengeIndex);
                                  }
                                }}
                                className={`w-full text-left flex items-center justify-between gap-3 py-2 px-3 rounded-lg transition-colors group ${isCurrentChallenge
                                  ? 'bg-orange-50 text-orange-700 border border-orange-100 dark:bg-orange-500/10 dark:text-orange-400 dark:border-orange-500/20'
                                  : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-800 dark:hover:text-gray-300'
                                  }`}
                              >
                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                  <span className={`flex-shrink-0 text-[10px] font-bold ${isCurrentChallenge ? 'text-orange-500' : 'text-gray-400 dark:text-gray-600'}`}>
                                    {challengeIndex + 1}.
                                  </span>
                                  <span className="text-xs truncate font-medium">
                                    {typeof challenge.title === 'string'
                                      ? challenge.title.replace(/^[📋🌎🔤🔝📊]\s*/, '')
                                      : challenge.title
                                    }
                                  </span>
                                </div>
                                {isChallengeCompleted && (
                                  <FaCheckCircle className="text-gray-300 dark:text-gray-600" size={10} />
                                )}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </nav>

              {/* Modal Footer */}
              <div className="p-4 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-[#0a1220]/50 flex-shrink-0">
                <div className="text-xs text-gray-500 text-center flex items-center justify-center gap-2">
                  <FaLightbulb className="text-amber-500" />
                  <span>Your progress is automatically saved</span>
                </div>
                <div className="mt-3 border-t border-gray-200 dark:border-white/5 pt-3">
                  <button
                    onClick={startTour}
                    className="text-xs font-bold text-orange-500 hover:text-orange-600 flex items-center justify-center gap-2 mx-auto transition-colors"
                  >
                    <HelpCircle size={12} /> Take a Tour
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Main Content Area - Full Screen */}
      <div className="h-screen flex flex-col overflow-hidden">
        {/* Top Bar - Fixed Height */}
        <div className="bg-white dark:bg-[#162032] border-b border-gray-200 dark:border-gray-800 px-4 lg:px-8 py-3 flex items-center justify-between flex-shrink-0 shadow-lg z-20 relative overflow-hidden transition-colors duration-300">
          <ParticleBackground particleCount={20} maxDist={75} speed={0.12} />
          {/* Left: Logo + Heading */}
          <div className="flex items-center gap-2 text-sm text-gray-500 flex-1 min-w-0">
            <Link to="/" className="flex items-center gap-2 group flex-shrink-0 mr-1">
              <img src={logo} alt="DataHelix" className="h-6 w-auto group-hover:rotate-[360deg] transition-transform duration-500 invert dark:invert-0" />
              <span className="font-bold text-gray-900 dark:text-white hidden lg:inline tracking-tight">DataHelix</span>
            </Link>
            <div className="h-5 w-px bg-gray-200 dark:bg-white/10 hidden sm:block flex-shrink-0 mr-1"></div>
            <Link
              to="/exercises"
              className="font-medium text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors flex-shrink-0 hidden sm:block"
            >
              Courses
            </Link>
            <FaChevronRight size={9} className="text-gray-400 dark:text-gray-600 flex-shrink-0 hidden sm:block" />
            {onTitleClick ? (
              <button
                onClick={onTitleClick}
                className="font-medium text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors cursor-pointer flex-shrink-0 hidden sm:block"
              >
                {title}
              </button>
            ) : (
              <span className="font-medium text-gray-500 dark:text-gray-400 flex-shrink-0 hidden sm:block">{title}</span>
            )}
            <FaChevronRight size={9} className="text-gray-400 dark:text-gray-600 flex-shrink-0 hidden sm:block" />
            <span className="font-bold text-gray-900 dark:text-white truncate">
              {cases[currentCase]?.title.replace(/^Case \d+:\s*/, '')}
            </span>
          </div>

          {/* Center: Cases Button with Challenge Navigation */}
          <div className="flex items-center gap-2 flex-shrink-0 ml-4">
            {/* Previous Challenge Button */}
            <button
              onClick={onPrevChallenge}
              disabled={!canGoPrev}
              className={`w-8 h-8 flex items-center justify-center rounded-lg transition-all ${canGoPrev
                ? 'bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-900 border border-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 dark:text-gray-300 dark:hover:text-white dark:border-gray-700'
                : 'bg-gray-50 border border-gray-100 text-gray-300 cursor-not-allowed dark:bg-gray-900 dark:border-gray-800 dark:text-gray-700'
                }`}
              title="Previous Challenge"
            >
              <FaArrowLeft size={12} />
            </button>

            {/* Cases Button - Opens Modal */}
            <button
              onClick={() => setSidebarOpen(true)}
              className="flex items-center gap-2 px-4 py-1.5 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-lg hover:shadow-lg hover:shadow-orange-500/20 transition-all font-bold text-xs tracking-wide uppercase leading-none h-8"
            >
              <FaBars size={12} className="relative -top-[0.5px]" />
              <span className="relative top-[0.5px]">Questions</span>
            </button>

            {/* Next Challenge Button */}
            <button
              onClick={onNextChallenge}
              disabled={!canGoNext}
              className={`w-8 h-8 flex items-center justify-center rounded-lg transition-all ${canGoNext
                ? 'bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-900 border border-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 dark:text-white dark:border-gray-700 dark:hover:border-gray-600'
                : 'bg-gray-50 border border-gray-100 text-gray-300 cursor-not-allowed dark:bg-gray-900 dark:border-gray-800 dark:text-gray-700'
                }`}
              title="Next Challenge"
            >
              <FaArrowRight size={12} />
            </button>
          </div>

          {/* Right: Tour button + Progress */}
          <div className="flex-1 hidden lg:flex justify-end ml-4 items-center gap-3">
            <button
              onClick={startTour}
              className="flex text-[10px] font-bold text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-500/10 px-2 py-1 rounded border border-orange-200 dark:border-orange-500/20 transition-colors gap-1 items-center"
            >
              <HelpCircle size={12} /> Tour
            </button>
            <div className="flex items-center gap-3 text-xs">
              <div className="flex flex-col items-end">
                <span className="text-gray-500 dark:text-gray-400 font-medium">Progress</span>
                <span className="text-orange-500 dark:text-orange-400 font-bold">{completionPercentage}%</span>
              </div>
              <div className="w-24 bg-gray-200 dark:bg-gray-800 h-1.5 rounded-full overflow-hidden">
                <div
                  className="bg-gradient-to-r from-orange-500 to-red-500 h-full rounded-full"
                  style={{ width: `${completionPercentage}%` }}
                />
              </div>
            </div>
            <div className="h-5 w-px bg-gray-200 dark:bg-white/10"></div>
            <ThemeToggle />
          </div>
        </div>

        {/* Content - Takes Remaining Space After Top Bar */}
        <div className="flex-1 overflow-hidden bg-gray-50 dark:bg-[#0a1220] transition-colors duration-300">
          {children}
        </div>
      </div>
    </div>
  );
};

export default ExerciseLayout;
